/**
 * EffectListEditor — a shared editor for a list of EffectRow objects.
 *
 * Used in two places:
 *   1) Counter rules editor (one list per CounterRule)
 *   2) Mechanics editor (one list per MechanicEntry)
 *
 * Each row models a single buff/debuff/loadout effect:
 *   direction → who is affected (self vs opponent vs loadout-self/opponent)
 *   target    → which slot/player receives the effect
 *   effect    → what stat/keyword/loadout state changes
 *   amount?   → numeric value (power/block/stamina/wear/weapon-count)
 *   keyword?  → keyword string (add-keyword/remove-keyword)
 *
 * Direction → target compatibility:
 *   - buff-self / debuff-opponent → action-slot targets, plus self/opponent
 *     player target for the `stamina` effect.
 *   - loadout-self / loadout-opponent → my/opp armor, weapons, sigs, champion.
 *
 * Target → effect compatibility:
 *   - self/opponent (player) → only `stamina`
 *   - my/opp armor          → `add-wear`, `remove-wear`
 *   - my/opp weapons        → `exhaust`, `ready` (with N picker)
 *   - my/opp sigs/champion  → no effects yet (placeholder)
 *   - action-slot targets   → `power`, `block`, `add-keyword`,
 *                             `remove-keyword`, `remove-power-buffs`,
 *                             `remove-block-buffs`, `stamina-cost`
 *
 * Context props let callers narrow the allowed action-slot targets:
 *   - allowThisTargets   → enable "this-attack/defend/action" (mechanics only)
 *   - allowPopupTargets  → enable "popup-attack/defend/action" (both)
 *   - allowNextTargets   → enable "my-next-*" targets
 */
import { Button } from "@/components/ui/Button";
import { Input, Label, Select } from "@/components/ui/Input";
import { ALL_KEYWORDS, keywordTakesAmount } from "@/game/rules/keywords";
import type {
  EffectDirection,
  EffectKind,
  EffectRow,
  EffectTarget,
} from "@/types/card";

interface EffectListEditorProps {
  value: EffectRow[];
  onChange: (rows: EffectRow[]) => void;
  /** When true, allow `this-*` targets (refers to the card itself once played).
   *  Mechanics use this; counters do not. */
  allowThisTargets?: boolean;
  /** When true, allow `popup-*` targets (refers to live combat slots).
   *  Counters use this; mechanics do not. */
  allowPopupTargets?: boolean;
  /** When true, allow `my-next-*` targets (the most common target — buff
   *  the player's next queued action). Default true. */
  allowNextTargets?: boolean;
  /** When true, allow loadout direction & loadout targets (mechanics only). */
  allowLoadoutTargets?: boolean;
  /** Adds a small contextual hint shown above the rows. */
  hint?: string;
}

type AmountKind =
  | "power"
  | "block"
  | "stamina-cost"
  | "stamina"
  | "draw"
  | "add-wear"
  | "remove-wear"
  | "exhaust"
  | "ready";
type KeywordKind = "add-keyword" | "remove-keyword";
type FlagKind = "remove-power-buffs" | "remove-block-buffs";

/** Effect kinds for action-slot targets (next/this/popup). */
const BUFF_KINDS_ACTION: EffectKind[] = [
  "power",
  "block",
  "add-keyword",
  "stamina-cost",
];

const DEBUFF_KINDS_ACTION: EffectKind[] = [
  "power",
  "block",
  "remove-keyword",
  "remove-power-buffs",
  "remove-block-buffs",
  "stamina-cost",
];

function isLoadoutDirection(d: EffectDirection): boolean {
  return d === "loadout-self" || d === "loadout-opponent";
}

function isPlayerTarget(t: EffectTarget): boolean {
  return t === "self" || t === "opponent";
}

function isLoadoutTarget(t: EffectTarget): boolean {
  return (
    t === "my-armor" ||
    t === "opp-armor" ||
    t === "my-weapons" ||
    t === "opp-weapons" ||
    t === "my-sigs" ||
    t === "opp-sigs" ||
    t === "my-champion" ||
    t === "opp-champion"
  );
}

function isWeaponTarget(t: EffectTarget): boolean {
  return t === "my-weapons" || t === "opp-weapons";
}

function isArmorTarget(t: EffectTarget): boolean {
  return t === "my-armor" || t === "opp-armor";
}

function isSigOrChampionTarget(t: EffectTarget): boolean {
  return (
    t === "my-sigs" ||
    t === "opp-sigs" ||
    t === "my-champion" ||
    t === "opp-champion"
  );
}

function isAmountKind(k: EffectKind): k is AmountKind {
  return (
    k === "power" ||
    k === "block" ||
    k === "stamina-cost" ||
    k === "stamina" ||
    k === "draw" ||
    k === "add-wear" ||
    k === "remove-wear" ||
    k === "exhaust" ||
    k === "ready"
  );
}

function isKeywordKind(k: EffectKind): k is KeywordKind {
  return k === "add-keyword" || k === "remove-keyword";
}

function isFlagKind(k: EffectKind): k is FlagKind {
  return k === "remove-power-buffs" || k === "remove-block-buffs";
}

/** Build the target option list given the row's direction + caller flags. */
function targetOptions(
  direction: EffectDirection,
  flags: {
    allowThisTargets: boolean;
    allowPopupTargets: boolean;
    allowNextTargets: boolean;
    allowLoadoutTargets: boolean;
  },
): { value: EffectTarget; label: string }[] {
  const opts: { value: EffectTarget; label: string }[] = [];

  // Loadout directions get a dedicated target list — armor, weapons, sigs,
  // champion. The "my-" or "opp-" prefix is locked to the direction so the
  // user can't build a buff-self + opponent's armor combo.
  if (direction === "loadout-self") {
    opts.push(
      { value: "my-armor", label: "my armor" },
      { value: "my-weapons", label: "my weapon(s)" },
      { value: "my-sigs", label: "my sig moves" },
      { value: "my-champion", label: "my champion" },
    );
    return opts;
  }
  if (direction === "loadout-opponent") {
    opts.push(
      { value: "opp-armor", label: "opponent's armor" },
      { value: "opp-weapons", label: "opponent's weapon(s)" },
      { value: "opp-sigs", label: "opponent's sig moves" },
      { value: "opp-champion", label: "opponent's champion" },
    );
    return opts;
  }

  // Regular (buff-self / debuff-opponent) directions.
  const owner = direction === "buff-self" ? "my" : "opponent's";

  // Player-level target — pairs with `stamina`. Show only the matching one.
  if (direction === "buff-self") {
    opts.push({ value: "self", label: "me (player)" });
  } else {
    opts.push({ value: "opponent", label: "opponent (player)" });
  }

  if (flags.allowNextTargets) {
    opts.push(
      { value: "my-next-attack", label: `${owner} next attack` },
      { value: "my-next-defend", label: `${owner} next defend` },
      { value: "my-next-action", label: `${owner} next action` },
    );
  }
  if (flags.allowThisTargets) {
    opts.push(
      { value: "this-attack", label: "this attack" },
      { value: "this-defend", label: "this defend" },
      { value: "this-action", label: "this action" },
    );
  }
  if (flags.allowPopupTargets) {
    opts.push(
      { value: "popup-attack", label: "popup: pending attack" },
      { value: "popup-defend", label: "popup: pending defend" },
      { value: "popup-action", label: "popup: pending action" },
    );
  }
  return opts;
}

/** Build the effect kind option list given the row's direction + target. */
function effectOptions(
  direction: EffectDirection,
  target: EffectTarget,
): EffectKind[] {
  // Loadout targets.
  if (isArmorTarget(target)) {
    return ["add-wear", "remove-wear"];
  }
  if (isWeaponTarget(target)) {
    return ["exhaust", "ready"];
  }
  if (isSigOrChampionTarget(target)) {
    // No effects yet — UI will render a placeholder.
    return [];
  }

  // Player-level target (self/opponent).
  if (isPlayerTarget(target)) return ["stamina", "draw", "search-deck", "clear-stun"];

  // Action-slot targets — standard effect set, minus stamina.
  const base =
    direction === "buff-self" ? BUFF_KINDS_ACTION : DEBUFF_KINDS_ACTION;
  // popup-* doesn't allow stamina-cost (cost is already paid).
  const isPopup = target.startsWith("popup-");
  return isPopup ? base.filter((k) => k !== "stamina-cost") : base;
}

function emptyRow(
  flags: {
    allowThisTargets: boolean;
    allowPopupTargets: boolean;
    allowNextTargets: boolean;
    allowLoadoutTargets: boolean;
  },
): EffectRow {
  const targets = targetOptions("buff-self", flags);
  const actionTarget =
    targets.find((t) => !isPlayerTarget(t.value))?.value ??
    targets[0]?.value ??
    "my-next-attack";
  return {
    direction: "buff-self",
    target: actionTarget,
    effect: "power",
    amount: 1,
  };
}

export function EffectListEditor({
  value,
  onChange,
  allowThisTargets = false,
  allowPopupTargets = false,
  allowNextTargets = true,
  allowLoadoutTargets = true,
  hint,
}: EffectListEditorProps) {
  const flags = {
    allowThisTargets,
    allowPopupTargets,
    allowNextTargets,
    allowLoadoutTargets,
  };

  function add() {
    onChange([...value, emptyRow(flags)]);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function patch(i: number, p: Partial<EffectRow>) {
    onChange(
      value.map((r, idx) => {
        if (idx !== i) return r;
        const next: EffectRow = { ...r, ...p };

        // Direction change: target list changes shape; pick a new target if
        // the current one is no longer in the list.
        if (p.direction !== undefined) {
          const opts = targetOptions(next.direction, flags);
          if (!opts.some((o) => o.value === next.target)) {
            // Try to keep the player-level pairing intact.
            if (isPlayerTarget(next.target)) {
              next.target =
                next.direction === "buff-self" ? "self" : "opponent";
            } else if (isLoadoutDirection(next.direction)) {
              next.target = opts[0]?.value ?? "my-armor";
            } else {
              next.target = opts[0]?.value ?? "my-next-attack";
            }
          }
        }

        // Target change: re-validate effect.
        if (p.target !== undefined) {
          const effOpts = effectOptions(next.direction, next.target);
          if (effOpts.length > 0 && !effOpts.includes(next.effect)) {
            next.effect = effOpts[0]!;
          }
        }

        // Direction change can also invalidate effect.
        if (p.direction !== undefined) {
          const effOpts = effectOptions(next.direction, next.target);
          if (effOpts.length > 0 && !effOpts.includes(next.effect)) {
            next.effect = effOpts[0]!;
          }
        }

        // Effect change → reset amount/keyword shape.
        if (
          p.effect !== undefined ||
          p.target !== undefined ||
          p.direction !== undefined
        ) {
          if (isAmountKind(next.effect)) {
            if (typeof next.amount !== "number") next.amount = 1;
            next.keyword = undefined;
          } else if (isKeywordKind(next.effect)) {
            next.keyword = next.keyword ?? ALL_KEYWORDS[0];
            next.amount = undefined;
            // Reset keywordAmount based on whether the keyword takes a value.
            // add-keyword + Reflect/Disarm need a numeric; remove-keyword never does.
            if (next.effect === "add-keyword" && keywordTakesAmount(next.keyword)) {
              if (typeof next.keywordAmount !== "number" || next.keywordAmount < 1) {
                next.keywordAmount = 1;
              }
            } else {
              next.keywordAmount = undefined;
            }
          } else if (isFlagKind(next.effect)) {
            next.amount = undefined;
            next.keyword = undefined;
            next.keywordAmount = undefined;
          }
        }

        // Keyword change — sync keywordAmount with the new keyword.
        if (p.keyword !== undefined && next.effect === "add-keyword") {
          if (keywordTakesAmount(next.keyword)) {
            if (typeof next.keywordAmount !== "number" || next.keywordAmount < 1) {
              next.keywordAmount = 1;
            }
          } else {
            next.keywordAmount = undefined;
          }
        }

        return next;
      }),
    );
  }

  return (
    <div className="space-y-2">
      {hint ? (
        <p className="text-xs text-[#64748b]">{hint}</p>
      ) : null}
      {value.length === 0 ? (
        <p className="text-xs italic text-[#475569]">No effects yet.</p>
      ) : null}
      {value.map((row, i) => {
        const targets = targetOptions(row.direction, flags);
        const allowedKinds = effectOptions(row.direction, row.target);
        const noEffectsForTarget = allowedKinds.length === 0;
        return (
          <div
            key={i}
            className="rounded border border-[#2a2f3a] bg-[#0f1115] p-2"
          >
            <div className="grid gap-2 sm:grid-cols-4">
              <div>
                <Label>Direction</Label>
                <Select
                  value={row.direction}
                  onChange={(e) =>
                    patch(i, { direction: e.target.value as EffectDirection })
                  }
                >
                  <option value="buff-self">Buff me</option>
                  <option value="debuff-opponent">Debuff opponent</option>
                  {allowLoadoutTargets ? (
                    <>
                      <option value="loadout-self">My loadout</option>
                      <option value="loadout-opponent">
                        Opponent's loadout
                      </option>
                    </>
                  ) : null}
                </Select>
              </div>
              <div>
                <Label>Target</Label>
                <Select
                  value={row.target}
                  onChange={(e) =>
                    patch(i, { target: e.target.value as EffectTarget })
                  }
                >
                  {targets.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Effect</Label>
                {noEffectsForTarget ? (
                  <p className="pt-2 text-xs italic text-[#64748b]">
                    No effects available for this target yet.
                  </p>
                ) : (
                  <Select
                    value={row.effect}
                    onChange={(e) =>
                      patch(i, { effect: e.target.value as EffectKind })
                    }
                  >
                    {allowedKinds.map((k) => (
                      <option key={k} value={k}>
                        {effectKindLabel(k, row.direction)}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
              <div>
                {noEffectsForTarget ? null : isAmountKind(row.effect) ? (
                  <>
                    <Label>{amountLabel(row.effect, row.target)}</Label>
                    <Input
                      type="number"
                      min={isWeaponTarget(row.target) ? 1 : 0}
                      value={row.amount ?? 0}
                      onChange={(e) =>
                        patch(i, { amount: Number(e.target.value) || 0 })
                      }
                    />
                    {isWeaponTarget(row.target) ? (
                      <p className="mt-1 text-[10px] text-[#64748b]">
                        Player picks any {row.amount ?? 0} weapon
                        {(row.amount ?? 0) === 1 ? "" : "s"} when this card
                        resolves.
                      </p>
                    ) : null}
                  </>
                ) : isKeywordKind(row.effect) ? (
                  <>
                    <Label>Keyword</Label>
                    <Select
                      value={row.keyword ?? ""}
                      onChange={(e) => patch(i, { keyword: e.target.value })}
                    >
                      <option value="">— any —</option>
                      {ALL_KEYWORDS.map((kw) => (
                        <option key={kw} value={kw}>
                          {kw}
                        </option>
                      ))}
                    </Select>
                    {row.effect === "add-keyword" && keywordTakesAmount(row.keyword) ? (
                      <div className="mt-2">
                        <Label>{row.keyword} value</Label>
                        <Input
                          type="number"
                          min={1}
                          value={row.keywordAmount ?? 1}
                          onChange={(e) =>
                            patch(i, {
                              keywordAmount: Math.max(
                                1,
                                Number(e.target.value) || 1,
                              ),
                            })
                          }
                        />
                        <p className="mt-1 text-[10px] text-[#64748b]">
                          Grants {row.keyword} {row.keywordAmount ?? 1}.
                        </p>
                      </div>
                    ) : null}
                  </>
                ) : row.effect === "search-deck" ? (
                  <>
                    <Label>Filter description</Label>
                    <input
                      type="text"
                      className="w-full rounded border border-[#2a2f3a] bg-[#0a0c10] px-2 py-1 text-sm text-white"
                      placeholder="e.g. Chain keyword card"
                      value={row.searchFilter ?? ""}
                      onChange={(e) => patch(i, { searchFilter: e.target.value || undefined })}
                    />
                    <div className="mt-2 flex gap-3">
                      <div className="flex-1">
                        <Label>Pick count</Label>
                        <Input
                          type="number" min={1} max={5}
                          value={row.searchPickCount ?? 1}
                          onChange={(e) => patch(i, { searchPickCount: Math.max(1, Number(e.target.value) || 1) })}
                        />
                      </div>
                      <div className="flex items-end gap-2 pb-1">
                        <input
                          type="checkbox"
                          id={`search-shuffle-${i}`}
                          checked={row.searchShuffleAfter !== false}
                          onChange={(e) => patch(i, { searchShuffleAfter: e.target.checked })}
                        />
                        <label htmlFor={`search-shuffle-${i}`} className="text-xs text-[#94a3b8]">
                          Shuffle after
                        </label>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Label>&nbsp;</Label>
                    <p className="pt-2 text-xs text-[#64748b]">
                      {row.effect === "remove-power-buffs"
                        ? "Strips queued +power buffs"
                        : "Strips queued +block buffs"}
                    </p>
                  </>
                )}
              </div>
            </div>
            {(() => {
              const help = helperText(
                row.direction,
                row.target,
                row.effect,
                row.keyword,
                row.amount,
                row.keywordAmount,
              );
              return help ? (
                <p className="mt-2 text-[11px] leading-snug text-[#94a3b8]">{help}</p>
              ) : null;
            })()}
            <div className="mt-2 text-right">
              <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                Remove effect
              </Button>
            </div>
          </div>
        );
      })}
      <Button variant="secondary" type="button" onClick={add}>
        + Add effect
      </Button>
    </div>
  );
}

function effectKindLabel(k: EffectKind, dir: EffectDirection): string {
  const sign = dir === "buff-self" ? "+" : "-";
  switch (k) {
    case "power":
      return `${sign}Power`;
    case "block":
      return `${sign}Block`;
    case "add-keyword":
      return "Add keyword";
    case "remove-keyword":
      return "Remove keyword";
    case "remove-power-buffs":
      return "Remove power buffs";
    case "remove-block-buffs":
      return "Remove block buffs";
    case "stamina-cost":
      return dir === "buff-self"
        ? "Reduce stamina cost"
        : "Increase stamina cost";
    case "stamina":
      return dir === "buff-self" ? "Gain X stamina" : "Lose X stamina";
    case "draw":
      return dir === "buff-self" ? "Draw X card(s)" : "Opponent draws X card(s)";
    case "search-deck":
      return "Search deck (tutor)";
    case "clear-stun":
      return "Clear stun (negate)";
    case "add-wear":
      return "Add wear counter";
    case "remove-wear":
      return "Remove wear counter";
    case "exhaust":
      return "Exhaust weapon(s)";
    case "ready":
      return "Ready weapon(s)";
  }
}

function amountLabel(k: AmountKind, target: EffectTarget): string {
  if (k === "power") return "Power amount";
  if (k === "block") return "Block amount";
  if (k === "stamina-cost") return "Cost amount";
  if (k === "stamina") return "Stamina amount";
  if (k === "draw") return "Cards to draw";
  if (k === "add-wear" || k === "remove-wear") return "Wear counters";
  if (k === "exhaust" || k === "ready") {
    if (isWeaponTarget(target)) return "How many weapons";
    return "Count";
  }
  return "Amount";
}

/** Per-row helper text shown beneath the controls — explains what the
 *  effect does in plain language. Keeps designers from guessing.
 *
 *  Patterns surfaced here mirror what's wired up across cards:
 *    - power / block on my-next-* (queued buff that fires on the next play)
 *    - power / block on this-* / popup-* (live mid-combat tweak)
 *    - stamina-cost on my-next-action (cost reduction stacks; e.g. Unbroken Sands)
 *    - add-keyword Quick / Stun / Reflect / Disarm (granted to next or this slot)
 *    - remove-power-buffs / remove-block-buffs (counter-style strip)
 *    - stamina (instant gain/loss; e.g. Hardened Steel +1 on damage dealt)
 *    - exhaust / ready / add-wear / remove-wear (loadout-state mutations)
 */
function helperText(
  direction: EffectDirection,
  target: EffectTarget,
  effect: EffectKind,
  keyword: string | undefined,
  amount: number | undefined,
  keywordAmount: number | undefined,
): string | null {
  const sign = direction === "buff-self" ? "+" : "−";
  const amt = amount ?? 0;
  const isNext = target.startsWith("my-next-");
  const isThis = target.startsWith("this-");
  const isPopup = target.startsWith("popup-");
  const slotKind = target.endsWith("-attack")
    ? "attack"
    : target.endsWith("-defend")
      ? "defend"
      : "action";

  if (effect === "power" || effect === "block") {
    const stat = effect === "power" ? "power" : "block";
    if (isNext) {
      return `Queues ${sign}${amt} ${stat} onto the player's next ${slotKind} when it's played.`;
    }
    if (isThis) {
      return `Bakes ${sign}${amt} ${stat} into THIS card as it's played.`;
    }
    if (isPopup) {
      return `Adjusts the live ${slotKind} on the table by ${sign}${amt} ${stat}.`;
    }
    return `${sign}${amt} ${stat}.`;
  }

  if (effect === "stamina-cost") {
    const word = direction === "buff-self" ? "Reduces" : "Increases";
    if (isNext) {
      return `${word} the cost of the next ${slotKind} by ${amt}. Stacks with other cost effects.`;
    }
    if (isThis) {
      return `${word} this card's printed cost by ${amt} when it resolves.`;
    }
    return `${word} cost by ${amt}.`;
  }

  if (effect === "stamina") {
    const word = direction === "buff-self" ? "Gain" : "Lose";
    return `${word} ${amt} stamina immediately when this fires.`;
  }

  if (effect === "add-keyword") {
    if (!keyword) return null;
    const valStr = keywordAmount && keywordAmount > 0 ? ` ${keywordAmount}` : "";
    if (isNext) {
      return `Grants ${keyword}${valStr} to the player's next ${slotKind}.`;
    }
    if (isThis) {
      return `Adds ${keyword}${valStr} to this card as it's played.`;
    }
    if (isPopup) {
      return `Adds ${keyword}${valStr} to the live ${slotKind} on the table.`;
    }
    return `Grants ${keyword}${valStr}.`;
  }

  if (effect === "remove-keyword") {
    if (!keyword) return null;
    return `Strips ${keyword} from the targeted ${slotKind}.`;
  }

  if (effect === "remove-power-buffs") {
    return `Reverts all queued +power buffs on the targeted ${slotKind} (counter-style).`;
  }
  if (effect === "remove-block-buffs") {
    return `Reverts all queued +block buffs on the targeted ${slotKind} (counter-style).`;
  }

  if (effect === "add-wear") return `Adds ${amt} wear counter${amt === 1 ? "" : "s"} to the armor.`;
  if (effect === "remove-wear") return `Removes up to ${amt} wear counter${amt === 1 ? "" : "s"} from the armor (clamps at 0).`;
  if (effect === "exhaust") return `Player picks any ${amt} weapon${amt === 1 ? "" : "s"} to exhaust (tap).`;
  if (effect === "ready") return `Player picks any ${amt} weapon${amt === 1 ? "" : "s"} to ready (untap).`;

  return null;
}
